/**
 * Shared tenant-scoped list query builders for unmigrated content modules.
 */

import {
  collection,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from 'firebase/firestore';
import { db } from '../firebase/firebase-config';
import { ROLES } from '../platform/constants';
import { effectiveRole } from '../platform/membershipPermissions';
import { getVisibleUnitIds } from './contentQueryHelpers';
import {
  filterByActiveTenant,
  shouldApplyServerTenantFilter,
} from './tenantContentFilter';

export function applyTenantScopeToCollection(collectionName, tenantId) {
  const coll = collection(db, collectionName);
  if (shouldApplyServerTenantFilter(tenantId)) {
    return query(coll, where('tenantId', '==', tenantId));
  }
  return coll;
}

export function canViewAllUnitsForTenant(user, memberships, tenantId) {
  const role = effectiveRole(memberships, tenantId, user);
  return role === ROLES.ADMIN || role === ROLES.LOCAL_ADMIN;
}

export function buildOrderedListQuery({
  collectionName,
  tenantId,
  user,
  memberships = [],
  orderField = 'order',
  orderDirection = 'asc',
  pageSize = null,
  lastVisible = null,
  unitField = 'unit',
}) {
  const scoped = applyTenantScopeToCollection(collectionName, tenantId);
  const constraints = [];

  if (user && !canViewAllUnitsForTenant(user, memberships, tenantId)) {
    constraints.push(where(unitField, 'in', getVisibleUnitIds(user)));
  }

  constraints.push(orderBy(orderField, orderDirection));

  if (lastVisible) {
    constraints.push(startAfter(lastVisible));
  }

  if (pageSize) {
    constraints.push(limit(pageSize));
  }

  return constraints.length > 0 ? query(scoped, ...constraints) : scoped;
}

export function buildCreatedAtListQuery({
  collectionName,
  tenantId,
  user,
  memberships = [],
  pageSize = null,
  lastVisible = null,
  unitField = 'unit',
}) {
  return buildOrderedListQuery({
    collectionName,
    tenantId,
    user,
    memberships,
    orderField: 'createdAt',
    orderDirection: 'desc',
    pageSize,
    lastVisible,
    unitField,
  });
}

export function mapAndFilterSnapshot(querySnapshot, tenantId, mapper) {
  const items = querySnapshot.docs.map(
    mapper || ((docSnap) => ({ id: docSnap.id, ...docSnap.data() })),
  );
  return filterByActiveTenant(items, tenantId);
}
